import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { UserContext } from '../../contexts/userProvider';
import { encontrarAlvo, TETO_PARA_ASSENTAR_MS } from './alvo';
import { useRetanguloDoAlvo } from './useRetanguloDoAlvo';
import { lerProgresso, lerVozLigada, marcarFim, salvarProgresso, salvarVozLigada } from './progresso';
import { disponivelPara, obterTutorial, passosPara, proximoTutorial } from './tutoriais';
import { TutorialContext } from './TutorialContext';
import Escurecido from './Escurecido';
import Seta from './Seta';
import Balao from './Balao';
import CartaoFinal from './CartaoFinal';
import './tutorial.css';

export const ROTA_DOS_TUTORIAIS = '/home/tutoriais';

/**
 * Clique que COMEÇA uma ação em vez de concluí-la (entrar num campo, abrir um
 * seletor): no passo `avancaAoClicar` ele não avança, senão o passo seguinte
 * taparia o que a pessoa acabou de abrir.
 */
const CAMPO_OU_SELETOR = 'input, textarea, select, [contenteditable="true"]';

/** Quanto a pessoa fica sem digitar para o passo de digitar seguir. */
const PAUSA_NA_DIGITACAO_MS = 1500;
/** O mesmo tempo da transição `.tutorial-desliza`, com folga. */
const DURACAO_DA_TROCA_MS = 350;
/**
 * Sem recorte ainda, o balão espera isto pelo alvo antes de aparecer no
 * centro: o alvo que existe aparece antes, e o balão nasce junto dele.
 */
const ESPERA_DO_BALAO_SEM_ALVO_MS = TETO_PARA_ASSENTAR_MS + 200;

/**
 * Traz o alvo para a vista quando ele assenta, antes da medida que vai para a
 * tela. Rolagem instantânea: a suave moveria o alvo com o recorte já na tela.
 */
const trazerParaATela = (alvo) => {
  const alto = alvo.getBoundingClientRect().height > window.innerHeight * 0.5;
  alvo.scrollIntoView?.({ block: alto ? 'start' : 'center', behavior: 'instant' });
};

const estaNosTutoriais = (pathname) => {
  try {
    return decodeURIComponent(pathname).toLowerCase() === ROTA_DOS_TUTORIAIS;
  } catch {
    return false;
  }
};

/**
 * Congela a rolagem da página enquanto o tutorial está aberto: rolando, o alvo
 * anda a cada quadro e o recorte fica correndo atrás dele. `passive: false` é
 * obrigatório, ou o navegador ignora o preventDefault. A rolagem do próprio
 * tutorial (scrollIntoView) continua funcionando.
 */
const useTravaDeRolagem = (ativa) => {
  useEffect(() => {
    if (!ativa) return undefined;
    const impedir = (evento) => evento.preventDefault();
    window.addEventListener('wheel', impedir, { passive: false });
    window.addEventListener('touchmove', impedir, { passive: false });
    return () => {
      window.removeEventListener('wheel', impedir);
      window.removeEventListener('touchmove', impedir);
    };
  }, [ativa]);
};

/**
 * Motor do tutorial guiado do ERP (porte do motor do Infinity, sem mascote).
 * Cada passo fala o seu texto com a voz gerada uma vez (`voz.js`); o balão
 * tem o botão de mudo.
 *
 * `voltarAosTutoriais` leva à página de tutoriais pelo mesmo caminho do menu
 * lateral; todo tutorial começa dali, com a tela limpa (sem modal aberto por
 * cima do menu). `abrirMenuLateral` abre o menu recolhido quando o passo
 * aponta um item dele.
 */
const TutorialProvider = ({ children, voltarAosTutoriais, abrirMenuLateral }) => {
  const { user, alert, isAdmin } = useContext(UserContext);
  const location = useLocation();
  const usuarioId = user?.id;

  const [progresso, setProgresso] = useState(() => lerProgresso(usuarioId));
  const [vozLigada, setVozLigada] = useState(() => lerVozLigada(usuarioId));
  useEffect(() => {
    setProgresso(lerProgresso(usuarioId));
    setVozLigada(lerVozLigada(usuarioId));
  }, [usuarioId]);

  const alternarVoz = useCallback(() => {
    setVozLigada((ligada) => {
      salvarVozLigada(usuarioId, !ligada);
      return !ligada;
    });
  }, [usuarioId]);

  const marcarConcluido = useCallback(
    (tutorialId, como) => {
      setProgresso((atual) => {
        const novo = marcarFim(atual, tutorialId, como);
        if (novo !== atual) salvarProgresso(usuarioId, novo);
        return novo;
      });
    },
    [usuarioId]
  );

  const [ativoId, setAtivoId] = useState(null);
  const [indice, setIndice] = useState(0);
  const [pulado, setPulado] = useState(false);
  const [passoMaisAdiante, setPassoMaisAdiante] = useState(0);
  const [seta, setSeta] = useState({ lado: 'bottom', oculta: true });

  const tutorial = ativoId ? obterTutorial(ativoId) : null;
  // Os passos deste nível: o que ensina tela só do Admin sai, e o "1/N" conta sem ele.
  const passos = useMemo(() => (tutorial ? passosPara(tutorial, isAdmin) : []), [tutorial, isAdmin]);
  const passo = passos[indice] ?? null;
  const emFinal = !!tutorial && indice >= passos.length;
  const proximo = emFinal ? proximoTutorial(tutorial.id, progresso.concluidos, isAdmin) : null;

  // Um aviso do ERP (o Alert) aberto durante o tutorial ficaria por baixo do
  // escurecido, sem como fechar. O tutorial sai da frente até ele fechar.
  const pausado = !!alert?.visible;

  const { rect, status } = useRetanguloDoAlvo(
    pausado || !passo ? null : passo.alvo,
    passo?.paciencia,
    trazerParaATela
  );

  useEffect(() => {
    setPassoMaisAdiante((mais) => Math.max(mais, indice));
  }, [indice]);

  useEffect(() => {
    if (passo?.alvo?.startsWith('menu:')) abrirMenuLateral?.();
  }, [passo?.alvo, abrirMenuLateral]);

  // Recorte, seta e balão deslizam só na troca de passo. Dentro do passo o
  // alvo também se move (rolagem, tela entrando), e com a transição ligada o
  // recorte ficava correndo atrás dele.
  const [trocandoDePasso, setTrocandoDePasso] = useState(false);
  const alvoAchado = status === 'achado';
  const [procurando, setProcurando] = useState(false);
  useEffect(() => {
    setProcurando(status === 'procurando');
  }, [status]);
  useEffect(() => {
    if (!ativoId) return undefined;
    setTrocandoDePasso(true);
    const fim = window.setTimeout(() => setTrocandoDePasso(false), DURACAO_DA_TROCA_MS);
    return () => window.clearTimeout(fim);
  }, [ativoId, indice, alvoAchado]);

  // Só desliza quem já estava na tela, e só dois quadros depois de aparecer:
  // na primeira aparição o balão partia do centro e escorregava até o alvo.
  const temRecorte = rect !== null;
  const [recorteNaTela, setRecorteNaTela] = useState(false);
  useEffect(() => {
    if (!temRecorte) {
      setRecorteNaTela(false);
      return undefined;
    }
    let segundoQuadro = 0;
    const primeiroQuadro = requestAnimationFrame(() => {
      segundoQuadro = requestAnimationFrame(() => setRecorteNaTela(true));
    });
    return () => {
      cancelAnimationFrame(primeiroQuadro);
      cancelAnimationFrame(segundoQuadro);
    };
  }, [temRecorte]);
  const animar = (trocandoDePasso || procurando) && recorteNaTela;

  // Sem recorte e ainda procurando, o balão espera o alvo em vez de nascer no
  // centro e escorregar até ele; se o alvo demora, aparece no centro.
  const chaveDoPasso = `${ativoId}:${indice}`;
  const esperandoOPrimeiroAlvo = !rect && status === 'procurando';
  const [balaoLiberadoNoPasso, setBalaoLiberadoNoPasso] = useState(null);
  useEffect(() => {
    if (!esperandoOPrimeiroAlvo) {
      setBalaoLiberadoNoPasso(null);
      return undefined;
    }
    const libera = window.setTimeout(() => setBalaoLiberadoNoPasso(chaveDoPasso), ESPERA_DO_BALAO_SEM_ALVO_MS);
    return () => window.clearTimeout(libera);
  }, [esperandoOPrimeiroAlvo, chaveDoPasso]);

  const pulandoOPasso = !!passo?.pulaSeAusente && status === 'ausente';
  const mostrarBalao =
    !pulandoOPasso && (!esperandoOPrimeiroAlvo || balaoLiberadoNoPasso === chaveDoPasso);

  /** O último passo terminou abrindo algo: o cartão final não pode tapá-lo. */
  const finalDiscreto = emFinal && !pulado && !!passos[passos.length - 1]?.exigeClique;

  useTravaDeRolagem(!!tutorial && !finalDiscreto && !pausado);

  const fechar = useCallback(() => {
    setPulado(false);
    setAtivoId(null);
    setIndice(0);
  }, []);

  /** Esc no meio do tutorial: conta como pulado e fecha. */
  const encerrar = useCallback(
    (tutorialId) => {
      marcarConcluido(tutorialId, 'pulado');
      fechar();
    },
    [marcarConcluido, fechar]
  );

  const iniciar = useCallback(
    (tutorialId) => {
      const escolhido = obterTutorial(tutorialId);
      if (!escolhido || !disponivelPara(escolhido, isAdmin)) return;
      if (!estaNosTutoriais(location.pathname)) voltarAosTutoriais?.();
      setPulado(false);
      setPassoMaisAdiante(0);
      setIndice(0);
      setAtivoId(tutorialId);
    },
    [location.pathname, voltarAosTutoriais, isAdmin]
  );

  const avancar = useCallback(() => {
    if (!tutorial) return;
    if (indice >= passos.length - 1) {
      // O fim do último passo não fecha: marca como feito e mostra o cartão
      // final, que oferece o próximo da trilha. O índice além do último passo
      // é o que representa esse estado.
      marcarConcluido(tutorial.id, 'feito');
      setIndice(passos.length);
      return;
    }
    setIndice((i) => i + 1);
  }, [tutorial, indice, passos.length, marcarConcluido]);

  useEffect(() => {
    if (pulandoOPasso) avancar();
  }, [pulandoOPasso, avancar]);

  const voltar = useCallback(() => setIndice((i) => Math.max(0, i - 1)), []);

  const pular = useCallback(() => {
    if (!tutorial) return;
    marcarConcluido(tutorial.id, 'pulado');
    setPulado(true);
    setIndice(passos.length);
  }, [tutorial, passos.length, marcarConcluido]);

  // Clique real no alvo avança o passo. O listener é de captura no document
  // para ver o clique antes de qualquer stopPropagation da tela.
  //
  // Detecta na captura, mas avança depois, numa macrotask: avançar ali mesmo
  // desmontava o alvo antes de o clique chegar ao React, e o onClick dele (o
  // que abre a tela, a aba, a janela) nunca rodava.
  const exigeClique = !!passo?.exigeClique;
  const cliqueAvanca = exigeClique || !!passo?.avancaAoClicar;
  const alvoDoPasso = passo?.alvo;
  useEffect(() => {
    if (!cliqueAvanca || !ativoId || !alvoDoPasso || pausado) return undefined;
    const aoClicar = (evento) => {
      const alvo = encontrarAlvo(alvoDoPasso);
      if (!alvo || !(evento.target instanceof Node) || !alvo.contains(evento.target)) return;
      if (!exigeClique && evento.target instanceof Element && evento.target.closest(CAMPO_OU_SELETOR)) return;
      setTimeout(avancar, 0);
    };
    document.addEventListener('click', aoClicar, true);
    return () => document.removeEventListener('click', aoClicar, true);
  }, [cliqueAvanca, exigeClique, alvoDoPasso, ativoId, avancar, pausado]);

  // Passo de digitar: segue sozinho quando a pessoa para de digitar no alvo.
  const digitacaoAvanca = !!passo?.avancaAoDigitar;
  useEffect(() => {
    if (!digitacaoAvanca || !ativoId || !alvoDoPasso || pausado) return undefined;
    let pausa;
    const aoDigitar = (evento) => {
      const alvo = encontrarAlvo(alvoDoPasso);
      if (!alvo || !(evento.target instanceof Node) || !alvo.contains(evento.target)) return;
      window.clearTimeout(pausa);
      pausa = window.setTimeout(avancar, PAUSA_NA_DIGITACAO_MS);
    };
    document.addEventListener('input', aoDigitar, true);
    return () => {
      document.removeEventListener('input', aoDigitar, true);
      window.clearTimeout(pausa);
    };
  }, [digitacaoAvanca, alvoDoPasso, ativoId, avancar, pausado]);

  // Esc sempre sai: é a saída quando o alvo não existe e a tela está bloqueada.
  useEffect(() => {
    if (!ativoId || pausado) return undefined;
    const aoTeclar = (evento) => {
      if (evento.key !== 'Escape') return;
      if (emFinal) fechar();
      else encerrar(ativoId);
    };
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [ativoId, emFinal, encerrar, fechar, pausado]);

  // Os painéis do ERP fecham com mousedown fora deles (o Filtros Avançados do
  // Estoque, os seletores). Clicar no balão seria "fora": o mousedown dentro
  // da camada do tutorial não chega ao document.
  const camadaRef = useRef(null);
  const montada = !!tutorial && (!!passo || emFinal) && !pausado;
  useEffect(() => {
    const camada = camadaRef.current;
    if (!montada || !camada) return undefined;
    const segurar = (evento) => evento.stopPropagation();
    camada.addEventListener('mousedown', segurar);
    return () => camada.removeEventListener('mousedown', segurar);
  }, [montada]);

  const valor = useMemo(
    () => ({ iniciar, ativoId, progresso, isAdmin: !!isAdmin }),
    [iniciar, ativoId, progresso, isAdmin]
  );

  return (
    <TutorialContext.Provider value={valor}>
      {children}
      {montada &&
        createPortal(
          <div ref={camadaRef} style={{ display: 'contents' }} data-tutorial-camada="">
            {(passo || !finalDiscreto) && (
              <Escurecido
                rect={passo ? rect : null}
                animar={animar}
                aoClicarNoRecorte={
                  passo && status === 'achado' && !cliqueAvanca && !digitacaoAvanca ? avancar : undefined
                }
              />
            )}
            {passo && rect && !seta.oculta && <Seta rect={rect} lado={seta.lado} animar={animar} />}
            {passo && mostrarBalao && (
              <Balao
                passo={passo}
                indice={indice}
                total={passos.length}
                rect={rect}
                status={status}
                animar={animar}
                jaFeito={indice < passoMaisAdiante}
                vozLigada={vozLigada}
                aoAlternarVoz={alternarVoz}
                aoVoltar={voltar}
                aoAvancar={avancar}
                aoPular={pular}
                aoMudarSeta={setSeta}
              />
            )}
            {emFinal && (
              <CartaoFinal
                tutorial={tutorial}
                proximo={proximo}
                pulado={pulado}
                discreto={finalDiscreto}
                aoComecarProximo={() => proximo && iniciar(proximo.id)}
                aoFechar={fechar}
              />
            )}
          </div>,
          document.body
        )}
    </TutorialContext.Provider>
  );
};

export default TutorialProvider;
